import {
  Button,
  Checkbox,
  CheckboxField,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
} from "@a7y/tailwind-catalyst";
import type { ContentSection, ContentStatus, DocsGuardRefusal } from "@a7y/types";
import { docsPagePath } from "@a7y/types";

/** The status a status-control action moves a post TOWARD. */
export type StatusControlTarget = ContentStatus;

interface StatusControlDialogProps {
  open: boolean;
  target: StatusControlTarget;
  /**
   * The post's status right now. Only `target === "draft"` branches on it:
   * restoring the last pre-archive snapshot when the post is `archived`,
   * otherwise reverting a live post to an unpublished draft.
   */
  currentStatus: ContentStatus;
  onClose: () => void;
  onConfirm: () => void;
  /**
   * "Queue in Publishing" (A7Y-4474 D15/plan §3 E): applies `target` as a
   * `pendingStatus` instead of running the transition now. Omitted where
   * queuing doesn't apply (e.g. the Publishing page's own quick actions,
   * which already act ON queued rows rather than creating one).
   */
  onQueue?: () => void;
  isPending: boolean;
  title: string;
  slug: string;
  /** Local datetime-local value (`yyyy-MM-ddTHH:mm`) — `target === "scheduled"` only. */
  goLiveLocal: string;
  onGoLiveChange: (value: string) => void;
  /** Defaults to "blog" so every pre-existing caller is unaffected. */
  section?: ContentSection;
  /**
   * Docs only (A7Y-4470 D6): the page's current live slug (`publishedSlug`).
   * When it differs from `slug`, publishing renames the live page, and the
   * redirect checkbox below appears.
   */
  livePublishedSlug?: string | null;
  /** Docs only: whether the rename writes a redirect. Checked by default. */
  redirectFromPreviousSlug?: boolean;
  onRedirectFromPreviousSlugChange?: (value: boolean) => void;
  /**
   * Docs only: the guard refusals from the last blocked attempt at this
   * transition (A7Y-4470 D5) — a broken link, a `DOCS_PAGES` key that would
   * stop resolving, or a public claim that would no longer be findable.
   */
  refusals?: DocsGuardRefusal[];
  /**
   * Docs only (Karen round 2 R2-F1): set when the fetch that determines
   * `livePublishedSlug` failed, so the caller couldn't tell whether this
   * publish renames the page. Shown in place of the redirect checkbox —
   * the checkbox silently not appearing on a fetch failure looks identical
   * to "this isn't a rename," which is the silent failure this discloses.
   */
  liveSlugLoadError?: string | null;
}

function refusalText(refusal: DocsGuardRefusal): string {
  switch (refusal.kind) {
    case "broken-link":
      return `Links to ${refusal.target}, which would not resolve.`;
    case "docs-pages-slug":
      return `The docs registry key "${refusal.key}" still points to ${refusal.slug} — it would stop resolving.`;
    case "claim":
      return `A public claim on ${refusal.slug} ("${refusal.textAnchor}") would no longer appear exactly once (found ${refusal.occurrences}).`;
    default:
      return "This action was refused.";
  }
}

/**
 * The bulleted refusal list this dialog renders, factored out so any other
 * surface that shows a docs guard refusal (e.g. `PublishingPage`'s bulk
 * publish, whose `api.docs.publishBatch` 409 carries the same
 * `DocsGuardRefusal[]` shape) renders it identically rather than
 * re-deriving the text (Karen round 2 R2-F3).
 */
export function RefusalList({ refusals }: { refusals: DocsGuardRefusal[] }) {
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5">
      {refusals.map((refusal, i) => (
        <li key={i}>{refusalText(refusal)}</li>
      ))}
    </ul>
  );
}

type DialogColor = "green" | "blue" | "amber" | "red";

/**
 * Replaces `PublishConfirmDialog` (A7Y-4474 D20/D26): one confirmation surface
 * for every status-control transition — publish, schedule, unpublish, restore
 * from archived, and archive — driven by the "Target → Call" table in the
 * ticket rather than a publish/schedule-only dialog. `content_service.ts`
 * decides which lifecycle route each transition calls; this component only
 * decides what to show and whether the confirm button is enabled.
 */
export function StatusControlDialog({
  open,
  target,
  currentStatus,
  onClose,
  onConfirm,
  onQueue,
  isPending,
  title,
  slug,
  goLiveLocal,
  onGoLiveChange,
  section = "blog",
  livePublishedSlug,
  redirectFromPreviousSlug = true,
  onRedirectFromPreviousSlugChange,
  refusals,
  liveSlugLoadError,
}: StatusControlDialogProps) {
  const isDocs = section === "docs";
  // Reading the clock during render is the point — the "is the chosen
  // go-live still in the future" check must be answered against now, not a
  // value frozen at mount, or Schedule would stay enabled for a time that has
  // since passed.
  // eslint-disable-next-line react-hooks/purity
  const now = Date.now();
  const goLiveInFuture = !!goLiveLocal && new Date(goLiveLocal).getTime() > now;
  const restoringFromArchive = target === "draft" && currentStatus === "archived";
  const noun = isDocs ? "page" : "post";
  const previewPath = isDocs ? docsPagePath(slug) : `/blog/posts/${slug || "…"}/`;
  // A7Y-4470 D6: publishing a docs page under a slug that differs from its
  // current live slug renames it, and the redirect checkbox only makes sense
  // for that transition.
  const isDocsRename =
    isDocs &&
    target === "published" &&
    !!livePublishedSlug &&
    livePublishedSlug !== slug;

  const heading =
    target === "published"
      ? `Publish ${noun}`
      : target === "scheduled"
        ? `Schedule ${noun}`
        : target === "archived"
          ? `Archive ${noun}`
          : restoringFromArchive
            ? `Restore ${noun}`
            : `Unpublish ${noun}`;

  const explanation =
    target === "published"
      ? `This makes the ${noun} live immediately and writes a revision snapshot.`
      : target === "scheduled"
        ? `The ${noun} will go live automatically at the scheduled time. It stays unpublished until then.`
        : target === "archived"
          ? `The ${noun} is taken down and marked archived. Its URL then answers 410 Gone rather than 404, and a revision snapshot is written.`
          : restoringFromArchive
            ? `This restores the ${noun} to draft from its last snapshot before it was archived. It does not go live again until you publish it.`
            : `The ${noun} will be taken down and reverted to draft.`;

  const confirmLabel = isPending
    ? target === "published"
      ? "Publishing…"
      : target === "scheduled"
        ? "Scheduling…"
        : target === "archived"
          ? "Archiving…"
          : restoringFromArchive
            ? "Restoring…"
            : "Unpublishing…"
    : target === "published"
      ? "Publish now"
      : target === "scheduled"
        ? "Schedule"
        : target === "archived"
          ? "Archive"
          : restoringFromArchive
            ? "Restore to draft"
            : "Unpublish";

  const confirmColor: DialogColor =
    target === "published"
      ? "green"
      : target === "scheduled"
        ? "blue"
        : target === "archived"
          ? "red"
          : "amber";

  const canConfirm = target !== "scheduled" || goLiveInFuture;

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogTitle>{heading}</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            {explanation}
          </div>

          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500 dark:text-zinc-400">Title</dt>
              <dd className="text-right font-medium text-zinc-900 dark:text-white">
                {title || "Untitled"}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500 dark:text-zinc-400">URL</dt>
              <dd className="text-right font-medium text-zinc-900 dark:text-white">
                {previewPath}
              </dd>
            </div>
          </dl>

          {target === "scheduled" && (
            <Field>
              <Label>Go-live time</Label>
              <Input
                type="datetime-local"
                className="mt-1.5"
                value={goLiveLocal}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onGoLiveChange(e.target.value)
                }
              />
              {!goLiveInFuture && goLiveLocal && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  The go-live time must be in the future.
                </p>
              )}
            </Field>
          )}

          {isDocs && target === "published" && liveSlugLoadError && (
            <p className="text-sm text-amber-700 dark:text-amber-400">{liveSlugLoadError}</p>
          )}

          {isDocsRename && (
            <CheckboxField>
              <Checkbox
                checked={redirectFromPreviousSlug}
                onChange={(checked: boolean) => onRedirectFromPreviousSlugChange?.(checked)}
              />
              <Label>
                Redirect {docsPagePath(livePublishedSlug ?? "")} to the new URL
              </Label>
            </CheckboxField>
          )}

          {refusals && refusals.length > 0 && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              <p className="font-semibold">This would break the following:</p>
              <RefusalList refusals={refusals} />
            </div>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        {onQueue && (
          <Button outline disabled={isPending || !canConfirm} onClick={onQueue}>
            Queue in Publishing
          </Button>
        )}
        <Button color={confirmColor} disabled={isPending || !canConfirm} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
